import { useEffect } from 'react';

type PageSEOProps = {
  title: string;
  description: string;
  canonicalPath: string;
  keywords?: string;
  jsonLd?: Record<string, unknown> | Record<string, unknown>[];
  noindex?: boolean;
};

const SITE_URL = import.meta.env.VITE_SITE_URL || 'https://cssbuyspreadsheet.wtf';

export default function PageSEO({ title, description, canonicalPath, keywords, jsonLd, noindex }: PageSEOProps) {
  const fullUrl = `${SITE_URL}${canonicalPath}`;

  useEffect(() => {
    const originalTitle = document.title;
    const metaDescription = document.querySelector('meta[name="description"]');
    const linkCanonical = document.querySelector('link[rel="canonical"]');
    const metaKeywords = document.querySelector('meta[name="keywords"]');
    const ogTitle = document.querySelector('meta[property="og:title"]');
    const ogDesc = document.querySelector('meta[property="og:description"]');
    const ogUrl = document.querySelector('meta[property="og:url"]');
    const metaRobots = document.querySelector('meta[name="robots"]');

    const originalDesc = metaDescription?.getAttribute('content') || '';
    const originalCanonical = linkCanonical?.getAttribute('href') || '';
    const originalKeywords = metaKeywords?.getAttribute('content') || '';
    const originalOgTitle = ogTitle?.getAttribute('content') || '';
    const originalOgDesc = ogDesc?.getAttribute('content') || '';
    const originalOgUrl = ogUrl?.getAttribute('content') || '';
    const originalRobots = metaRobots?.getAttribute('content') || '';

    document.title = title;
    if (metaDescription) metaDescription.setAttribute('content', description);
    if (linkCanonical) linkCanonical.setAttribute('href', fullUrl);
    if (metaKeywords && keywords) metaKeywords.setAttribute('content', keywords);
    if (ogTitle) ogTitle.setAttribute('content', title);
    if (ogDesc) ogDesc.setAttribute('content', description);
    if (ogUrl) ogUrl.setAttribute('content', fullUrl);

    if (noindex) {
      if (metaRobots) {
        metaRobots.setAttribute('content', 'noindex, nofollow');
      } else {
        const newRobots = document.createElement('meta');
        newRobots.name = 'robots';
        newRobots.content = 'noindex, nofollow';
        document.head.appendChild(newRobots);
      }
    }

    return () => {
      document.title = originalTitle;
      if (metaDescription) metaDescription.setAttribute('content', originalDesc);
      if (linkCanonical) linkCanonical.setAttribute('href', originalCanonical);
      if (metaKeywords) metaKeywords.setAttribute('content', originalKeywords);
      if (ogTitle) ogTitle.setAttribute('content', originalOgTitle);
      if (ogDesc) ogDesc.setAttribute('content', originalOgDesc);
      if (ogUrl) ogUrl.setAttribute('content', originalOgUrl);
      if (metaRobots) {
        metaRobots.setAttribute('content', originalRobots);
      } else if (noindex) {
        const el = document.querySelector('meta[name="robots"]');
        if (el) el.remove();
      }
    };
  }, [title, description, fullUrl, keywords, noindex]);

  useEffect(() => {
    if (!jsonLd) return;

    const schemas = Array.isArray(jsonLd) ? jsonLd : [jsonLd];
    const selector = 'script[data-json-ld="true"]';
    const existingScripts = document.querySelectorAll(selector);
    existingScripts.forEach(s => s.remove());

    schemas.forEach((schema) => {
      const script = document.createElement('script');
      script.setAttribute('data-json-ld', 'true');
      script.type = 'application/ld+json';
      script.textContent = JSON.stringify(schema);
      document.head.appendChild(script);
    });

    return () => {
      const els = document.querySelectorAll(selector);
      els.forEach(el => el.remove());
    };
  }, [jsonLd]);

  return null;
}